import { useEffect, useState } from "react";

import { Button, Checkbox, CheckboxGroup, Divider, Flex, FormControl, FormErrorMessage, FormLabel, Input, Switch, Textarea, useDisclosure, useToast } from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import { COLORS, DURATION_TOAST, REGISTRATION_STATUS } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectReviewer from "@/components/Selects/SelectReviewer";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { inspectorSchema } from "@/schemas/inspectorSchema";
import { getInspectorById, postInspector, putInspector } from "@/services/inspectorService";

import '../styles/Form.css';
import EditFile from "@/components/Fields/EditFile";

const EditInspector = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id, returnPath } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [defaultType, setDefaultType] = useState<string[]>([]);

    const navigateBack = () => {
        navigate(returnPath || '/manage-inspecting-company');
    }

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Save Inspecting Company Data?");
        setConfirmationTest("Are you sure you want to save?");
        setConfirmButtonText("Save Company Data");
        setCancelButtonText("Cancel");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataEdit = () => {
        const data = new FormData();
        if (id){
            data.append('InspectorId', id);
        }
        data.append('Sirebire', dataEdit.type.includes('SIRE | BIRE'));
        data.append('Vetting', dataEdit.type.includes('Vetting'));
        data.append('isReviewer', dataEdit.reviewer);
        data.append('RegisterStatus', String(dataEdit.status || !id ? REGISTRATION_STATUS.ACTIVE : REGISTRATION_STATUS.INACTIVE));
        data.append('CompanyName', dataEdit.companyName);
        data.append('Address', dataEdit.address.trim());
        data.append('Country', dataEdit.country);
        data.append('ContactPerson', dataEdit.contactPerson);
        data.append('Email', dataEdit.email);
        data.append('AlternateEmail', dataEdit.alternateEmail);
        data.append('Phone', dataEdit.phone);
        data.append('DGT1File', dataEdit.dgt1 || '');
        data.append('CORFile', dataEdit.cor || '');
        data.append('CompanyProfileFile', dataEdit.companyProfile || '');
        data.append('FinanceReportsFile', dataEdit.latestFinancialReport || '');
        data.append('BankDetailFile', dataEdit.bankDetails || '');
        data.append('NIBBusRegFile', dataEdit.NIB || '');
        data.append('ModifiedBy', name);

        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title.startsWith("Cancel")) {
            navigateBack();
            return;
        }

        const obj = generateDataEdit();
        let response: any
        if (id){
            response = await putInspector(obj);
        } else {
            response = await postInspector(obj);
        }
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Inspecting company saved.',
                description: "We've saved inspecting company data for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigateBack();
            }, 1000);
        } else {
            toast({
                title: 'Inspecting company failed to save.',
                description: "We could not save inspecting company data for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const getDataDetail = async () => {
        const response = await getInspectorById(id) as any;
        if (response) {
            setDataDetail(response.data);

            const newDefaultType: string[] = [];

            if (response.data.sirebire) {
                newDefaultType.push('SIRE | BIRE');
            }

            if (response.data.vetting) {
                newDefaultType.push('Vetting');
            }

            setDefaultType(newDefaultType);
        }
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        companyName: dataDetail?.companyName ?? '',
                        type: dataDetail ? defaultType : [],
                        reviewer: (id ? (dataDetail?.isReviewer ? 'true' : 'false') : ''),
                        address: dataDetail?.address ?? '',
                        country: dataDetail?.country ?? '',
                        contactPerson: dataDetail?.contactPerson ?? '',
                        email: dataDetail?.email ?? '',
                        alternateEmail: dataDetail?.alternateEmail ?? '',
                        phone: dataDetail?.phone ?? '',
                        status: dataDetail?.status === REGISTRATION_STATUS.ACTIVE,
                    }}
                    validationSchema={() => inspectorSchema(true)}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='companyName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.companyName && form.touched.companyName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Company Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Company Name' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.companyName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='type'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.type && form.touched.type)} marginBottom={4}>
                                        <Flex alignItems="center">
                                            <FormLabel htmlFor="type" className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CheckboxGroup
                                                    value={props.values.type}
                                                    onChange={(value: string[]) => props.setFieldValue('type', value)}
                                                >
                                                    <Flex gap={8}>
                                                        <Checkbox value="SIRE | BIRE">SIRE | BIRE</Checkbox>
                                                        <Checkbox value="Vetting">Vetting</Checkbox>
                                                    </Flex>
                                                </CheckboxGroup>
                                                <FormErrorMessage>{String(form.errors.type)}</FormErrorMessage>
                                            </div>
                                        </Flex>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='reviewer'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.reviewer && form.touched.reviewer)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Reviewer<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectReviewer name='reviewer' handleChange={({ value }: any) => props.setFieldValue('reviewer', value)} value={props.values.reviewer} hasError={form.errors.reviewer && form.touched.reviewer} defaultValue={props.values.reviewer} />
                                                <FormErrorMessage>{String(form.errors.reviewer)}</FormErrorMessage>
                                            </div>
                                        </div>
                                        
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='address'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.address && form.touched.address)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Address<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <Textarea {...field} placeholder='Address' className="form-input" />
                                        </div>
                                        <FormErrorMessage>{String(form.errors.address)}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='country'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.country && form.touched.country)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} defaultValue={props.values.country} />
                                                <FormErrorMessage>{String(form.errors.country)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='contactPerson'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.contactPerson && form.touched.contactPerson)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Contact Person<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Contact Person' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.contactPerson)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='email'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.email && form.touched.email)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Nomination Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Nomination Email' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.email)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='alternateEmail'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.alternateEmail && form.touched.alternateEmail)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Invoice Email' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.alternateEmail)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='phone'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.phone && form.touched.phone)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Phone Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Phone Number' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.phone)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='dgt1'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.dgt1 && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">DGT-1<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        fileId={dataDetail?.dgT1}
                                                        handleDrop={(e) => props.setFieldValue('dgt1', e[0])}
                                                        isInvalid={Boolean(form.errors.dgt1 && form.submitCount)}
                                                        type="registration-inspectingcompany"
                                                        isDisabled={props.isSubmitting}
                                                        title="DGT-1 File"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.dgt1)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='cor'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.cor && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">COR<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        fileId={dataDetail?.cor}
                                                        handleDrop={(e) => props.setFieldValue('cor', e[0])} //handleDrop(e, props.setFieldValue, 'cor')}
                                                        isInvalid={Boolean(form.errors.cor && form.submitCount)}
                                                        type="registration-inspectingcompany"
                                                        isDisabled={props.isSubmitting}
                                                        title="COR File"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.cor)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='companyProfile'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.companyProfile && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Company Profile</FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        fileId={dataDetail?.companyProfile}
                                                        handleDrop={(e) => props.setFieldValue('companyProfile', e[0])} //handleDrop(e, props.setFieldValue, 'companyProfile')}
                                                        isInvalid={Boolean(form.errors.companyProfile && form.submitCount)}
                                                        type="registration-inspectingcompany"
                                                        isDisabled={props.isSubmitting}
                                                        title="Company Profile File"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.companyProfile)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='latestFinancialReport'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.latestFinancialReport && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Latest Financial Report</FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        fileId={dataDetail?.financeReports}
                                                        handleDrop={(e) => props.setFieldValue('latestFinancialReport', e[0])} //handleDrop(e, props.setFieldValue, 'latestFinancialReport')}
                                                        isInvalid={Boolean(form.errors.latestFinancialReport && form.submitCount)}
                                                        type="registration-inspectingcompany"
                                                        isDisabled={props.isSubmitting}
                                                        title="Latest Financial Report File"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.latestFinancialReport)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='bankDetails'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.bankDetails && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Bank Details</FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        fileId={dataDetail?.bankDetail}
                                                        handleDrop={(e) => props.setFieldValue('bankDetails', e[0])} //handleDrop(e, props.setFieldValue, 'bankDetails')}
                                                        isInvalid={Boolean(form.errors.bankDetails && form.submitCount)}
                                                        type="registration-inspectingcompany"
                                                        isDisabled={props.isSubmitting}
                                                        title="Bank Details File"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.bankDetails)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='NIB'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.NIB && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">NIB / Company Bussiness Registration</FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        fileId={dataDetail?.nibBusReg}
                                                        handleDrop={(e) => props.setFieldValue('NIB', e[0])} //handleDrop(e, props.setFieldValue, 'NIB')}
                                                        isInvalid={Boolean(form.errors.NIB && form.submitCount)}
                                                        type="registration-inspectingcompany"
                                                        isDisabled={props.isSubmitting}
                                                        title="NIB File"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.NIB)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            {Boolean(id) && <Field name='status'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.status && form.touched.status)} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Status
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={props.values.status} onChange={() => props.setFieldValue('status', !props.values.status)} id='status-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div >
        </>
    )
}

export default EditInspector;
